import { useMemo } from "react";
import {
  Box,
  Flex,
  IconButton,
  Text,
  useDisclosure,
  Center,
  UnorderedList,
  ListItem,
  Link,
} from "@chakra-ui/react";
import { isEmpty } from "lodash";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";

import { Suggestion, Word } from "utils/types";
import { LIMIT_SEARCH } from "utils/constants";
import { apis } from "utils/config";
import { generateSuggestionKey, generateSuggestionUrl } from "utils/functions";
import { getCorrection } from "apis";

import FadeIn from "./FadeIn";
import FilterModal from "./FilterModal";
import CollocateItem from "./CollocateItem";
import Pagination from "./Pagination";
import Loading from "./Loading";

import { ReactComponent as FilterIcon } from "../assets/filter_icon.svg";
import { ReactComponent as FilledFilterIcon } from "../assets/filter_filled_icon.svg";

interface SearchResultProps {
  data: {
    word: string;
    collocates: Word[];
    total: number;
    matched_pos: string[];
  };
  isLoading: boolean;
  currentPage: number;
  collocatePos: string[];
  filters: string[];
  onSearch: ({
    filters,
    collocatePos,
  }: {
    filters: string[];
    collocatePos: string[];
  }) => void;
  onChangePage: (page: number) => void;
  onSubmit: (value: Suggestion) => void;
  keyword?: string;
}

const SearchResult = ({
  data,
  isLoading,
  currentPage,
  collocatePos,
  filters,
  onSearch,
  onChangePage,
  onSubmit,
  keyword,
}: SearchResultProps) => {
  const { word, collocates, total, matched_pos } = data;
  const { isOpen, onToggle } = useDisclosure();

  const { data: correctionData, isFetching: isFetchingCorrection } = useQuery({
    queryKey: [apis.correction(keyword).key, keyword],
    queryFn: () => getCorrection(keyword as string),
    enabled: !!keyword && isEmpty(collocates),
  });
  let shouldShowNotFound = useMemo(() => {
    const data = correctionData?.data;
    if (data?.length > 0) {
      return !!data?.find((o: Suggestion) => o.word === keyword);
    }
    return false;
  }, [correctionData?.data, keyword]);
  const hasNoFilter = !collocatePos?.length && !filters?.length;

  return (
    <FadeIn>
      <Flex alignItems="flex-end">
        <Flex color="primary">
          <Text fontSize="3xl">{word}</Text>
          {!isEmpty(matched_pos) && (
            <Text fontSize="1rem">{matched_pos.join(",")}</Text>
          )}
        </Flex>
        <IconButton
          fill="primary"
          ml="2rem"
          variant="ghost"
          onClick={onToggle}
          aria-label="Filter"
          icon={hasNoFilter ? <FilterIcon /> : <FilledFilterIcon />}
          _hover={{ bg: "transparent", transform: "scale(1.1)" }}
        />
      </Flex>
      {isLoading || isFetchingCorrection ? (
        <Loading />
      ) : (
        <Box mt="2">
          {isEmpty(collocates) && !shouldShowNotFound ? (
            <>
              <Text color="grayTitle" as="h6" size="md" mb="1rem">
                Are you looking for
              </Text>
              <UnorderedList pl="1rem">
                {correctionData?.data?.map((o: Suggestion) => {
                  return (
                    <ListItem key={generateSuggestionKey(o)} color="grayTxt">
                      <Link as={RouterLink} to={generateSuggestionUrl(o)}>
                        <Box display="flex">
                          <Text fontSize="sm">{o.word}</Text>
                          {o.pos && (
                            <Text ml="0.1rem" fontSize="0.6rem">
                              {o.pos}
                            </Text>
                          )}
                        </Box>
                      </Link>
                    </ListItem>
                  );
                })}
              </UnorderedList>
            </>
          ) : (
            <>
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(total / LIMIT_SEARCH)}
                onPageChange={(page: number) => onChangePage(page)}
              />
              {isEmpty(collocates) ? (
                <Center>No data</Center>
              ) : (
                collocates.map((o) => {
                  return (
                    <CollocateItem
                      key={o.id}
                      base={word}
                      data={o}
                      onSubmit={onSubmit}
                    />
                  );
                })
              )}
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(total / LIMIT_SEARCH)}
                onPageChange={(page: number) => onChangePage(page)}
              />
            </>
          )}
        </Box>
      )}
      {!!isOpen && (
        <FilterModal
          isOpen={isOpen}
          collocatePos={collocatePos}
          filters={filters}
          onClose={onToggle}
          onSearch={onSearch}
        />
      )}
    </FadeIn>
  );
};

export default SearchResult;
